import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { bannerApi, settingsApi } from '../../shared/api/endpoints';
import TransparentLogo from '../../shared/components/TransparentLogo';
import { mediaUrl, resolveSiteMedia } from '../../shared/utils/mediaUrl';
import { sampleImageEdgeColor } from '../../shared/utils/sampleImageColor';
import { FABRIC_SLIDES } from '../../shared/constants/fabricSlides';
import defaultLogo from '../../shared/assets/logo.png';
import './HeroBanner.css';

const ROTATE_MS = 5500;

export default function HeroBanner() {
  const [adminBanners, setAdminBanners] = useState([]);
  const [logoSrc, setLogoSrc] = useState(defaultLogo);
  const [active, setActive] = useState(0);
  const [blendRgb, setBlendRgb] = useState('255, 245, 240');

  const slides = useMemo(() => {
    if (adminBanners.length > 0) {
      return adminBanners.map((b) => ({
        id: b._id,
        image: b.image,
        title: b.title,
        subtitle: b.subtitle,
        ctaText: b.ctaText,
        ctaLink: b.ctaLink || '/products',
      }));
    }
    return FABRIC_SLIDES;
  }, [adminBanners]);

  const current = slides[active] || slides[0];
  const imageOnly = Boolean(
    current &&
      !String(current.title || '').trim() &&
      !String(current.subtitle || '').trim() &&
      !String(current.ctaText || '').trim()
  );

  useEffect(() => {
    bannerApi
      .list()
      .then((res) => setAdminBanners(res.data.data || []))
      .catch(() => setAdminBanners([]));

    settingsApi
      .get()
      .then((res) => {
        const s = res.data.data;
        const src = resolveSiteMedia(s, 'brandLogo') || resolveSiteMedia(s, 'introLogo');
        if (src) setLogoSrc(src);
      })
      .catch(() => {});
  }, []);

  const syncBlendColor = useCallback(async (imageSrc) => {
    const { rgb } = await sampleImageEdgeColor(imageSrc);
    setBlendRgb(rgb);
  }, []);

  useEffect(() => {
    if (current?.image) syncBlendColor(current.image);
  }, [current?.image, syncBlendColor]);

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const timer = setInterval(() => {
      setActive((i) => (i + 1) % slides.length);
    }, ROTATE_MS);
    return () => clearInterval(timer);
  }, [slides.length]);

  useEffect(() => {
    setActive(0);
  }, [slides.length]);

  return (
    <section
      className={`hero-banner${imageOnly ? ' hero-banner--image-only' : ''}`}
      style={{ '--hero-blend-rgb': blendRgb }}
    >
      <div className="hero-banner__carousel" aria-hidden>
        <AnimatePresence mode="sync">
          <motion.div
            key={current?.id || active}
            className="hero-banner__slide"
            style={{ backgroundImage: `url(${current?.image})` }}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.1, ease: 'easeInOut' }}
          />
        </AnimatePresence>
      </div>

      <div className="hero-banner__tint" aria-hidden />

      <div className="hero-banner__left">
        <TransparentLogo
          src={logoSrc}
          alt="NAYAVARNAM"
          className="hero-banner__logo"
          asMotion
          MotionComponent={motion.img}
          motionProps={{
            initial: { opacity: 0, scale: 0.92 },
            animate: { opacity: 1, scale: 1 },
            transition: { duration: 0.9 },
          }}
        />
      </div>

      <div className="hero-banner__right">
        {!imageOnly && (
          <AnimatePresence mode="wait">
            <motion.div
              key={`copy-${current?.id}-${active}`}
              className="hero-banner__story"
              initial={{ opacity: 0, x: 28 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="hero-banner__eyebrow">NAYAVARNAM EDIT</span>
              <div className="hero-banner__rule" aria-hidden />
              {current?.title && <h1 className="hero-banner__title">{current.title}</h1>}
              {current?.subtitle && <p className="hero-banner__lede">{current.subtitle}</p>}
              {current?.ctaText && (
                <Link to={current.ctaLink || '/products'} className="hero-banner__cta">
                  <span>{current.ctaText}</span>
                  <em aria-hidden>→</em>
                </Link>
              )}
            </motion.div>
          </AnimatePresence>
        )}

        {slides.length > 1 && (
          <div className="hero-banner__dots">
            {slides.map((s, i) => (
              <button
                key={s.id}
                type="button"
                className={i === active ? 'is-active' : ''}
                onClick={() => setActive(i)}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
